import { FunctionComponent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useApiClientWrapper } from 'hooks';
import styles from './AddEditEvaluationReport.module.scss';

// Redux State & Actions
import { fetchEvaluationServiceThunk } from 'actions/services/ServicesActions';
import { getEvaluationReportThunk, ReportsStatus, saveEvaluationReportThunk } from '@spike/reports-action';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { showSuccess } from '@spike/notifications-action';

// Models & Types
import Service from 'model/Service';
import { AppointmentPet } from '@spike/appointment-model';
import { dispatch, RootState } from 'store';
import { EvaluationReport } from '@spike/report-model';

// UI Components
import { Spinner } from 'components/UI';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import Button from 'components/UI/V3/Button/Button';
import { Textarea } from 'components/UI/V3/Forms/Textarea/Textarea';

// Internal Components
import OrderDetailHeader from '../OrderDetailHeader';
import PetInfo from '../PetDetail/PetInfo';
import { CustomEvaluationQuestion } from './CustomEvaluationQuestion';
import { FinalResults } from './FinalResults';
import { hasAtLeastOneFinalResultSelected } from './Validations';
import Pet from '@spike/pet-model';

export interface AddEditEvaluationReportProps {
    evaluationReportId?: number;
    evaluationReport?: EvaluationReport;
    appointmentId: number;
    serviceId: number;
    pet: AppointmentPet;
    onBack?: () => void;
    onClose?: () => void;
    onSave?: (evaluationReport: EvaluationReport) => void;
    className?: string;
}

export const AddEditEvaluationReport: FunctionComponent<AddEditEvaluationReportProps> = props => {
    const apiClientWrapper = useApiClientWrapper();
    const evaluationService = useSelector<RootState, Service | null | undefined>(
        state => state.services.evaluationService
    );
    const serviceStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);
    const reportStatus = useSelector((state: RootState) => state.reports.status);
    const [loadingSave, setLoadingSave] = useState(false);
    const [loadingFetch, setLoadingFetch] = useState(true);

    const [evaluationReport, setEvaluationReport] = useState<EvaluationReport | null>(null);
    const [cleanedReport, setCleanedReport] = useState<EvaluationReport | null>(null);

    const evaluationReportId = props.evaluationReportId;

    useEffect(() => {
        if (!evaluationService && serviceStatus !== ServicesStatus.Fetching) {
            dispatch(fetchEvaluationServiceThunk());
        }
    }, [evaluationService, serviceStatus]);

    useEffect(() => {
        const shouldMerge = !evaluationReport && evaluationService?.evaluation;

        const allQuestions = evaluationService?.evaluation?.evaluationQuestions || [];

        if (props.evaluationReport && shouldMerge) {
            const existingResponses = props.evaluationReport.customQuestionResponse || [];

            const mergedResponses = allQuestions.map(q => {
                const existing = existingResponses.find(r => r.customQuestionId === q.id);
                return {
                    id: existing?.id,
                    customQuestionId: Number(q.id),
                    questionSnapshot: existing?.questionSnapshot ?? q.questionText,
                    answer: existing?.answer ?? ''
                };
            });

            setEvaluationReport({
                ...props.evaluationReport,
                customQuestionResponse: mergedResponses
            });

            setLoadingFetch(false);
            return;
        }

        if (props.evaluationReportId != null && shouldMerge) {
            setLoadingFetch(true);
            dispatch(getEvaluationReportThunk(apiClientWrapper, props.evaluationReportId))
                .then((report: EvaluationReport) => {
                    const existingResponses = report.customQuestionResponse || [];

                    const mergedResponses = allQuestions.map(q => {
                        const existing = existingResponses.find(r => r.customQuestionId === q.id);
                        return {
                            customQuestionId: Number(q.id),
                            questionSnapshot: existing?.questionSnapshot ?? q.questionText,
                            answer: existing?.answer ?? ''
                        };
                    });

                    setEvaluationReport({
                        ...report,
                        customQuestionResponse: mergedResponses
                    });
                })
                .catch(() => setEvaluationReport(null))
                .finally(() => setLoadingFetch(false));
            return;
        }

        if (
            !props.evaluationReportId &&
            shouldMerge &&
            props.pet.id &&
            props.appointmentId &&
            evaluationService.evaluation!.id
        ) {
            const mergedResponses = allQuestions.map(q => ({
                customQuestionId: Number(q.id),
                questionSnapshot: q.questionText,
                answer: ''
            }));

            setEvaluationReport({
                appointmentId: props.appointmentId,
                petId: props.pet.id,
                evaluationId: evaluationService.evaluation!.id,
                serviceId: props.serviceId,
                notes: '',
                finalResultGrooming: '',
                finalResultDaycare: '',
                finalResultBoarding: '',
                customQuestionResponse: mergedResponses,
                createdAt: '',
                bookedAt: '',
                updatedAt: ''
            });

            setLoadingFetch(false);
        }
    }, [
        props.evaluationReport,
        props.evaluationReportId,
        props.pet.id,
        props.appointmentId,
        evaluationService,
        evaluationReport
    ]);

    useEffect(() => {
        if (reportStatus === ReportsStatus.SaveEvaluationSuccess) {
            setLoadingSave(false);
            dispatch(showSuccess('Report saved'));
            if (props.onSave && cleanedReport) props.onSave(cleanedReport);
        }

        if (reportStatus === ReportsStatus.Error) {
            setLoadingSave(false);
        }
    }, [reportStatus]);

    const shouldAutoPassFinalResults = (report: EvaluationReport): boolean => {
        return report.customQuestionResponse.every(r => r.answer === 'pass' || r.answer === 'conditional_pass');
    };

    const isValid = hasAtLeastOneFinalResultSelected(evaluationReport);

    if (loadingFetch || serviceStatus === ServicesStatus.Fetching || !evaluationService) {
        return (
            <div className={styles.loadingContainer}>
                <Spinner className={styles.loading} />
            </div>
        );
    }

    const title = (evaluationReportId !== null && evaluationReportId !== undefined) || evaluationReport;

    return (
        <div className={styles.container}>
            <div className={styles.headerContainer}>
                <OrderDetailHeader
                    title={title ? 'Edit Results' : 'Add Results'}
                    onBack={props.onBack}
                    withoutOptions={true}
                    onEdit={() => {}}
                    onRebook={() => {}}
                    onShowLog={() => {}}
                />
            </div>

            <OverlayScrollbarsComponent className={styles.scrollContainer}>
                <div className={styles.petInfoContainer}>
                    <PetInfo pet={props.pet as unknown as Pet} />
                </div>
                {evaluationService.evaluation?.evaluationQuestionsEnabled && (
                    <div className={styles.customQuestionContainer}>
                        {evaluationReport?.customQuestionResponse.map((q, idx) => (
                            <CustomEvaluationQuestion
                                key={q.customQuestionId}
                                questionText={q.questionSnapshot}
                                questionId={q.customQuestionId}
                                selectedAnswer={q.answer}
                                onChange={(id, answer) => {
                                    setEvaluationReport(prev => {
                                        if (!prev) return prev;

                                        const updatedResponses = prev.customQuestionResponse.map(r =>
                                            r.customQuestionId === id ? { ...r, answer } : r
                                        );

                                        const updatedReport = {
                                            ...prev,
                                            customQuestionResponse: updatedResponses
                                        };

                                        if (shouldAutoPassFinalResults(updatedReport)) {
                                            updatedReport.finalResultGrooming = 'pass';
                                            updatedReport.finalResultDaycare = 'pass';
                                            updatedReport.finalResultBoarding = 'pass';
                                        }

                                        return updatedReport;
                                    });
                                }}
                            />
                        ))}
                    </div>
                )}

                <div className={styles.finalResultContainer}>
                    {evaluationReport && (
                        <FinalResults
                            evaluationReport={evaluationReport}
                            categories={evaluationService?.evaluation?.category ?? []}
                            onChange={(field: string, value: string) => {
                                setEvaluationReport(prev => (prev ? { ...prev, [field]: value } : prev));
                            }}
                        />
                    )}
                </div>
                <div className={styles.evaluationNotesContainer}>
                    {evaluationReport && (
                        <Textarea
                            label="Evaluation Notes"
                            value={evaluationReport.notes}
                            maxLength={500}
                            placeholder="Notes here..."
                            onChange={e =>
                                setEvaluationReport(prev => (prev ? { ...prev, notes: e.target.value } : prev))
                            }
                            className={styles.notesTextarea}
                        />
                    )}
                </div>
            </OverlayScrollbarsComponent>

            <div className={styles.footerContainer}>
                <Button
                    label="Save"
                    variant="green"
                    size="large"
                    fullWidth={true}
                    disabled={!isValid}
                    isLoading={loadingSave}
                    onClick={() => {
                        if (evaluationReport) {
                            const cleanedReport = {
                                ...evaluationReport,
                                customQuestionResponse: evaluationReport.customQuestionResponse.filter(r => !!r.answer)
                            };

                            setCleanedReport(cleanedReport);
                            setLoadingSave(true);
                            dispatch(saveEvaluationReportThunk(apiClientWrapper, cleanedReport));
                        }
                    }}
                    className={styles.saveButton}
                />
            </div>
        </div>
    );
};
